import _debounce from 'lodash/debounce';
import type React from 'react';
import { useMemo, useRef, useState } from 'react';
import useControlledState from '../../hooks/useControlledState';
import PopoverButton from './PopoverButton';
import PopoverContent from './PopoverContent';
import PopoverContext from './PopoverContext';
import type { PopoverProps } from './types';

const MOUSE_EVENT_DELAY_IN_MS = 200;

function Popover(props: PopoverProps) {
  const {
    children,
    onOpenChange: onChange,
    open: controlledValue,
    initialValue = false,
    anchorEl: initialAnchorEl,
    onClose,
    triggerType = 'click',
  } = props;
  const [isVisible, toggleVisibility] = useControlledState({
    controlledValue,
    defaultValue: initialValue,
    onChange,
  });
  const isMouseInsideRef = useRef(false);
  // ref to track if the popover was just opened via long press
  const longPressJustOpenedRef = useRef(false);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(initialAnchorEl ?? null);
  const value = useMemo(() => {
    const onMouseMove =
      triggerType !== 'hover'
        ? undefined
        : _debounce((event: React.MouseEvent) => {
            if (event.type === 'mouseenter') {
              isMouseInsideRef.current = true;
              return toggleVisibility(true);
            }
            if (event.type === 'mouseleave') {
              isMouseInsideRef.current = false;
              return toggleVisibility(false);
            }
          }, MOUSE_EVENT_DELAY_IN_MS);

    return {
      anchorEl,
      onClose: () => {
        // can't close the popover if the mouse is still inside the popover
        if (triggerType === 'hover' && isMouseInsideRef.current) {
          return;
        }
        toggleVisibility(false);
        onClose?.();
      },
      onOpen: () => toggleVisibility(true),
      onOpenChange: toggleVisibility,
      open: Boolean(isVisible),
      setAnchorEl,
      triggerType,
      onMouseMove,
      longPressJustOpenedRef,
    };
  }, [anchorEl, toggleVisibility, isVisible, triggerType, onClose]);

  return (
    <PopoverContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </PopoverContext.Provider>
  );
}

Popover.displayName = 'Popover';

const PopoverNamespace = Object.assign(Popover, {
  Button: PopoverButton,
  Content: PopoverContent,
});

export default PopoverNamespace;
